import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../../api/axios';
import toast from 'react-hot-toast';

const PaymentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('invoiceId');
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    invoiceId: invoiceId || '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'Cash',
    cash_account_id: '',
    description: '',
    received_against: '',
    received_description: ''
  });

  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const cashAccounts = accounts.filter(acc => {
    try {
      const name = (acc.name || '').toLowerCase();
      return acc.type === 'ASSET' && 
             (name.includes('cash') || name.includes('bank') || name.includes('till'));
    } catch (error) {
      return false;
    }
  });
  const incomeAccounts = accounts.filter(acc => acc.type === 'INCOME');

  useEffect(() => {
    fetchInvoices();
    fetchAccounts();
    setupCashAccountsIfNeeded();
    if (invoiceId) {
      handleInvoiceChange(invoiceId);
    }
    if (isEdit) {
      fetchPayment();
    }
  }, [id, invoiceId]);

  const setupCashAccountsIfNeeded = async () => {
    if (cashAccounts.length === 0) {
      try {
        await axios.post('/api/accounts/setup-cash');
        toast.success('Default cash accounts created');
        fetchAccounts();
      } catch (error) {
      }
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/accounts/chart-of-accounts');
      setAccounts(response.data || []);
    } catch (error) {
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get('/api/invoices');
      const unpaidInvoices = (response.data.data || response.data || []).filter(
        invoice => invoice.paymentStatus !== 'Paid'
      );
      setInvoices(unpaidInvoices);
    } catch (error) {
      toast.error('Failed to load invoices');
    }
  };

  const fetchPayment = async () => {
    try {
      const response = await axios.get(`/api/payments/${id}`);
      const payment = response.data;
      setFormData({
        invoiceId: payment.invoiceId || '',
        date: payment.date.split('T')[0],
        amount: payment.amount,
        payment_method: payment.payment_method,
        cash_account_id: payment.cash_account_id || '',
        description: payment.description || '',
        received_against: payment.received_against || '',
        received_description: payment.received_description || ''
      });
      if (payment.invoiceId) {
        handleInvoiceChange(payment.invoiceId);
      }
    } catch (error) {
      toast.error('Failed to load payment');
    }
  };

  const handleInvoiceChange = async (invoiceId) => {
    if (!invoiceId) {
      setSelectedInvoice(null);
      setFormData(prev => ({ ...prev, invoiceId: '', amount: '', description: '', received_description: '' }));
      return;
    }

    try {
      const response = await axios.get(`/api/invoices/${invoiceId}`);
      const invoice = response.data;
      setSelectedInvoice(invoice);
      
      const remainingAmount = (invoice.total || 0) - (invoice.paidAmount || 0);
      setFormData(prev => ({
        ...prev,
        invoiceId,
        amount: remainingAmount.toFixed(2),
        description: `Payment received from ${invoice.customer?.name || 'customer'}`,
        received_description: `Invoice ${invoice.invoiceNumber || `INV-${invoice.id}`}`
      }));
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (formData.invoiceId) {
        // Record payment against invoice
        const paymentData = {
          amount: parseFloat(formData.amount),
          paymentMode: formData.payment_method,
          referenceNo: '',
          notes: formData.description,
          cashAccountId: formData.cash_account_id
        };
        await axios.patch(`/api/invoices/${formData.invoiceId}/payments`, paymentData);
      } else {
        // Record general incoming payment
        await axios.post('/api/payments/incoming', formData);
      }
      toast.success('Incoming payment recorded successfully');
      if (formData.invoiceId) {
        navigate(`/accounts/invoices/${formData.invoiceId}`);
      } else {
        navigate('/accounts', { state: { activeTab: 'payments' } });
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Record Incoming Payment
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow border p-6">
          <h3 className="text-lg font-semibold mb-4">Record Incoming Payment</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice (Optional)</label>
              <select
                value={formData.invoiceId}
                onChange={(e) => handleInvoiceChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Invoice (or leave blank for general payment)</option>
                {Array.isArray(invoices) && invoices.map(invoice => (
                  <option 
                    key={invoice.id} 
                    value={invoice.id}
                    title={`Customer: ${invoice.customer?.name || 'N/A'} | Total: $${invoice.total || 0} | Paid: $${invoice.paidAmount || 0}`}
                  >
                    {invoice.invoiceNumber || `INV-${invoice.id}`} - ${invoice.customer?.name} (Remaining: ${((invoice.total || 0) - (invoice.paidAmount || 0)).toFixed(2)})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                step="0.01"
                min="0"
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {selectedInvoice && (
                <p className="text-sm text-gray-600 mt-1">
                  Remaining: ${((selectedInvoice.total || 0) - (selectedInvoice.paidAmount || 0)).toFixed(2)}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
              <select
                name="payment_method"
                value={formData.payment_method}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="Bank">Bank Transfer</option>
                <option value="Online">Online Payment</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cash Account *</label>
              <select
                name="cash_account_id"
                value={formData.cash_account_id}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Cash Account</option>
                {cashAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} (${parseFloat(account.balance || 0).toFixed(2)})
                  </option>
                ))}
              </select>
            </div>

            {!formData.invoiceId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Received Against *</label>
                <select
                  name="received_against"
                  value={formData.received_against}
                  onChange={handleChange}
                  required={!formData.invoiceId}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Income Account</option>
                  {incomeAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="e.g., Received from customer"
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Received Description *</label>
              <input
                type="text"
                name="received_description"
                value={formData.received_description}
                onChange={handleChange}
                placeholder="e.g., Invoice #1234"
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {selectedInvoice && (
            <div className="p-4 bg-gray-50 rounded-lg mt-4">
              <h4 className="text-md font-medium text-gray-900 mb-2">Invoice Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Customer:</span> {selectedInvoice.customer?.name}
                </div>
                <div>
                  <span className="font-medium">Invoice Date:</span> {new Date(selectedInvoice.invoiceDate).toLocaleDateString()}
                </div>
                <div>
                  <span className="font-medium">Total Amount:</span> ${(parseFloat(selectedInvoice.total) || 0).toFixed(2)}
                </div>
                <div>
                  <span className="font-medium">Paid Amount:</span> ${(parseFloat(selectedInvoice.paidAmount) || 0).toFixed(2)}
                </div>
                <div>
                  <span className="font-medium">Remaining:</span> ${((parseFloat(selectedInvoice.total) || 0) - (parseFloat(selectedInvoice.paidAmount) || 0)).toFixed(2)}
                </div>
                <div>
                  <span className="font-medium">Status:</span> 
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                    selectedInvoice.paymentStatus === 'Paid' ? 'bg-green-100 text-green-800' :
                    selectedInvoice.paymentStatus === 'Partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {selectedInvoice.paymentStatus}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex space-x-3 mt-6">
            <button
              type="button"
              onClick={() => navigate('/accounts')}
              className="px-4 py-2 border rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Recording...' : (formData.invoiceId ? 'Record Invoice Payment' : 'Record Payment')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;
